import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from '@cian/ui-kit';
import { TButtonTheme } from '@cian/ui-kit/button/types';
import { Microfrontend } from '@cian/mf-react';

import { requestAuthentication } from '../../actions/authentication';
import { BrokerFallbackModal } from '../../components/BrokerFallbackModal';
import { getFirstGaLabel } from '../../selectors/analytics';
import { selectBrokerConsultantBannerButtonText } from '../../selectors/brokerConsultantBanner';
import { getUser } from '../../selectors/user';
import { TTypedThunkDispatch } from '../../types/thunk';
import { IAuthenticatedUser } from '../../types/user';
import { useFallbackModalSettings, useMicrofrontAPI, useMicrofrontEvents, useRegion } from './hooks';
import { trackBannerClick } from './tracking';
import { TOpenMicrofrontResultHandler } from './types';

interface IBrokerBannerButtonContainerProps {
  theme?: TButtonTheme;
}

export const BrokerBannerButtonContainer: React.FC<IBrokerBannerButtonContainerProps> = ({ theme }) => {
  const user = useSelector(getUser) as IAuthenticatedUser;
  const gaLabel = useSelector(getFirstGaLabel);
  const region = useRegion();

  const [isLoading, setIsLoading] = React.useState(false);
  const buttonText = useSelector(selectBrokerConsultantBannerButtonText);
  const fallbackModalSettings = useFallbackModalSettings();

  const [isFallbackModalVisible, setFallbackModalVisibility] = React.useState(false);
  const closeFallbackModal = React.useCallback(() => setFallbackModalVisibility(false), []);
  const showFallbackModal = React.useCallback(() => setFallbackModalVisibility(true), []);

  const dispatch: TTypedThunkDispatch = useDispatch();

  const finishMicrofrontOpening = React.useCallback<TOpenMicrofrontResultHandler>(
    ({ isError }) => {
      if (isError) {
        showFallbackModal();
        setIsLoading(false);
      } else {
        if (isFallbackModalVisible) {
          closeFallbackModal();
        }

        // снятие loading произойдет по событию из микрофронта.
      }
    },
    [isFallbackModalVisible, showFallbackModal, closeFallbackModal],
  );

  const { openMicrofront, microfrontManifest, microfrontRef } = useMicrofrontAPI(finishMicrofrontOpening);

  useMicrofrontEvents(microfrontManifest, setIsLoading);

  // Реф нужен, чтобы получать свежего user в коллбэке во избежание проблемы с замыканием на старое значение.
  const userRef = React.useRef(user);

  React.useEffect(() => {
    userRef.current = user;
  }, [user]);

  const startMicrofrontOpening = React.useCallback(() => {
    setIsLoading(true);
    openMicrofront({ user: userRef.current, region, gaLabel });
  }, [region, gaLabel, openMicrofront]);

  const handleButtonClick = React.useCallback(() => {
    trackBannerClick();

    if (!user.isAuthenticated) {
      dispatch(
        requestAuthentication({
          onSuccess: () => startMicrofrontOpening,
        }),
      );

      return;
    }

    startMicrofrontOpening();
  }, [dispatch, user, startMicrofrontOpening]);

  return (
    <>
      <Button theme={theme} loading={isLoading} onClick={handleButtonClick}>
        {buttonText}
      </Button>
      {isFallbackModalVisible && (
        <BrokerFallbackModal
          {...fallbackModalSettings}
          loading={isLoading}
          onClose={closeFallbackModal}
          onRetry={startMicrofrontOpening}
        />
      )}
      {microfrontManifest && (
        <Microfrontend
          ref={microfrontRef}
          name={microfrontManifest.microfrontendName}
          image={microfrontManifest.imageVersion}
          runtime={microfrontManifest.runtimeName}
          instance={microfrontManifest.instanceId}
        />
      )}
    </>
  );
};
